import { useState, useEffect, useMemo, useCallback } from 'react'
import { useAuthFetch } from '../lib/useAuthFetch'
import { MAPBOX_TOKEN } from '../components/map/mapStyle'

const API = import.meta.env.VITE_API_URL ?? ''

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReviewItem {
  id: string
  dispensary_id: string
  platform_slug: string
  change_type: 'new_listing' | 'name' | 'location' | 'business_type' | 'delisted'
  field: string | null
  old_value: unknown
  new_value: unknown
  status: string
  created_at: string
  dispensary_name: string
  city: string
  county: string
  lat: number | null
  lng: number | null
  listing_status: string
}

interface DataRequestItem {
  id: string
  status: 'pending' | 'reviewing'
  created_at: string
  competitor_name: string
  organization_name: string
  website_url: string | null
  last_observed_at: string | null
}

// ─── Config ───────────────────────────────────────────────────────────────────

const GROUP_ORDER = ['new_listing', 'name', 'location', 'business_type', 'delisted'] as const

const GROUP_LABELS: Record<string, string> = {
  new_listing:   'New Listings',
  name:          'Name Changes',
  location:      'Location Changes',
  business_type: 'Business Type Changes',
  delisted:      'Delisted',
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function formatVal(val: unknown, changeType: string): string {
  if (val === null || val === undefined) return '—'
  if (changeType === 'location') {
    const v = (typeof val === 'string' ? JSON.parse(val) : val) as { lat: number; lng: number }
    return `${Number(v.lat).toFixed(5)}, ${Number(v.lng).toFixed(5)}`
  }
  if (typeof val === 'object') return JSON.stringify(val).replace(/^"|"$/g, '')
  return String(val).replace(/^"|"$/g, '')
}

function staticMapUrl(oldVal: unknown, newVal: unknown): string {
  if (!MAPBOX_TOKEN) return ''
  try {
    const o = (typeof oldVal === 'string' ? JSON.parse(oldVal) : oldVal) as { lat: number; lng: number }
    const n = (typeof newVal === 'string' ? JSON.parse(newVal) : newVal) as { lat: number; lng: number }
    const overlay = [
      `pin-s+d4537e(${o.lng},${o.lat})`,
      `pin-s+1d9e75(${n.lng},${n.lat})`,
    ].join(',')
    return `https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/${overlay}/auto/280x120@2x?padding=40&access_token=${MAPBOX_TOKEN}`
  } catch {
    return ''
  }
}

// ─── Sub-component: item row ──────────────────────────────────────────────────

function ItemRow({
  item,
  resolving,
  onAction,
}: {
  item: ReviewItem
  resolving: boolean
  onAction: (id: string, action: 'approve' | 'reject') => void
}) {
  const isLocation = item.change_type === 'location'
  const mapUrl = isLocation ? staticMapUrl(item.old_value, item.new_value) : ''

  return (
    <div style={rowStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: 'var(--text-1)', fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 500 }}>
            {item.dispensary_name}
          </div>
          <div style={{ color: 'var(--text-2)', fontFamily: 'var(--sans)', fontSize: 12, marginTop: 2 }}>
            {item.city}{item.county ? `, ${item.county}` : ''}
            {' · '}
            <span style={{ fontFamily: 'var(--mono)' }}>{timeAgo(item.created_at)}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            style={approveBtnStyle(resolving)}
            onClick={() => !resolving && onAction(item.id, 'approve')}
            disabled={resolving}
          >
            {resolving ? '…' : 'Approve'}
          </button>
          <button
            style={rejectBtnStyle(resolving)}
            onClick={() => !resolving && onAction(item.id, 'reject')}
            disabled={resolving}
          >
            Reject
          </button>
        </div>
      </div>

      {/* Diff */}
      {(item.change_type === 'name' || item.change_type === 'business_type') && (
        <div style={diffRowStyle}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-2)', textDecoration: 'line-through' }}>
            {formatVal(item.old_value, item.change_type)}
          </span>
          <span style={{ color: 'var(--text-2)', margin: '0 8px', fontSize: 12 }}>→</span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent-intel)' }}>
            {formatVal(item.new_value, item.change_type)}
          </span>
        </div>
      )}

      {item.change_type === 'new_listing' && (
        <div style={diffRowStyle}>
          <span style={{ fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--text-2)' }}>
            Already live on the map — approve to acknowledge
          </span>
        </div>
      )}

      {item.change_type === 'delisted' && (
        <div style={diffRowStyle}>
          <span style={{ fontFamily: 'var(--sans)', fontSize: 12, color: 'var(--accent-block)' }}>
            Platform removed this listing — approve to confirm removal, reject to keep pin live
          </span>
        </div>
      )}

      {isLocation && (
        <div style={{ marginTop: 8 }}>
          {mapUrl ? (
            <img
              src={mapUrl}
              alt="Location change preview"
              style={{ width: 280, height: 120, borderRadius: 4, display: 'block', border: '1px solid var(--border)' }}
              loading="lazy"
            />
          ) : (
            <div style={diffRowStyle}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-2)' }}>
                {formatVal(item.old_value, 'location')}
              </span>
              <span style={{ color: 'var(--text-2)', margin: '0 8px', fontSize: 12 }}>→</span>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent-intel)' }}>
                {formatVal(item.new_value, 'location')}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ReconciliationReview() {
  const authFetch = useAuthFetch()
  const [items, setItems] = useState<ReviewItem[]>([])
  const [dataRequests, setDataRequests] = useState<DataRequestItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [resolving, setResolving] = useState<Set<string>>(new Set())
  const [bulkCounty, setBulkCounty] = useState('')
  const [bulkType, setBulkType] = useState('')
  const [bulkConfirming, setBulkConfirming] = useState(false)
  const [bulkWorking, setBulkWorking] = useState(false)
  const [visibleByGroup, setVisibleByGroup] = useState<Record<string, number>>({})

  const fetchItems = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [listingRes, requestRes] = await Promise.all([
        authFetch(`${API}/api/v1/admin/reconciliation?status=pending&limit=500`),
        authFetch(`${API}/api/v1/admin/reconciliation/data-requests`),
      ])
      const [listingData, requestData] = await Promise.all([listingRes.json(), requestRes.json()])
      if (!listingData.success) throw new Error(listingData.error || 'Failed to load listing queue')
      if (!requestData.success) throw new Error(requestData.error || 'Failed to load data requests')
      setItems(listingData.data.items)
      setDataRequests(requestData.data.items)
    } catch {
      setError('Network error — could not reach the API')
    } finally {
      setLoading(false)
    }
  }, [authFetch])

  useEffect(() => { fetchItems() }, [fetchItems])

  const grouped = useMemo(() => {
    const map: Record<string, ReviewItem[]> = {}
    for (const ct of GROUP_ORDER) map[ct] = []
    for (const item of items) {
      if (map[item.change_type]) map[item.change_type].push(item)
    }
    return GROUP_ORDER
      .map(ct => ({ key: ct, label: GROUP_LABELS[ct], items: map[ct] }))
      .filter(g => g.items.length > 0)
  }, [items])

  const counties = useMemo(() =>
    [...new Set(items.map(i => i.county).filter(Boolean))].sort(), [items])

  const bulkCount = useMemo(() =>
    items.filter(i =>
      (!bulkCounty || i.county === bulkCounty) &&
      (!bulkType || i.change_type === bulkType)
    ).length, [items, bulkCounty, bulkType])

  const handleAction = async (id: string, action: 'approve' | 'reject') => {
    setResolving(prev => new Set([...prev, id]))
    try {
      const res = await authFetch(
        `${API}/api/v1/admin/reconciliation/${id}/${action}`,
        { method: 'POST' }
      )
      const data = await res.json()
      if (data.success) setItems(prev => prev.filter(i => i.id !== id))
      else setError(data.error || `Failed to ${action} item`)
    } catch {
      setError('Network error')
    } finally {
      setResolving(prev => { const s = new Set(prev); s.delete(id); return s })
    }
  }

  const handleBulkApprove = async () => {
    setBulkWorking(true)
    try {
      const body: Record<string, string> = {}
      if (bulkCounty) body.county = bulkCounty
      if (bulkType) body.change_type = bulkType
      const res = await authFetch(
        `${API}/api/v1/admin/reconciliation/bulk-approve`,
        { method: 'POST', body: JSON.stringify(body) }
      )
      const data = await res.json()
      if (data.success) {
        await fetchItems()
        setBulkConfirming(false)
        setBulkCounty('')
        setBulkType('')
      } else {
        setError(data.error || 'Bulk approve failed')
      }
    } catch {
      setError('Network error')
    } finally {
      setBulkWorking(false)
    }
  }

  const handleDataRequest = async (id: string, status: 'reviewing' | 'resolved' | 'dismissed') => {
    setResolving(prev => new Set([...prev, id]))
    try {
      const res = await authFetch(`${API}/api/v1/admin/reconciliation/data-requests/${id}`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      })
      const payload = await res.json()
      if (!payload.success) throw new Error(payload.error || 'Update failed')
      if (status === 'reviewing') {
        setDataRequests(prev => prev.map(item => item.id === id ? { ...item, status } : item))
      } else {
        setDataRequests(prev => prev.filter(item => item.id !== id))
      }
    } catch (err: any) {
      setError(err?.message || 'Network error')
    } finally {
      setResolving(prev => { const next = new Set(prev); next.delete(id); return next })
    }
  }

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={{ color: 'var(--text-2)', fontFamily: 'var(--sans)', fontSize: 14 }}>
          Pulling listing changes from the review queue…
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <h1 style={headingStyle}>Listing Review</h1>
        <div style={{ color: 'var(--danger)', fontFamily: 'var(--sans)', fontSize: 13, marginTop: 16 }}>
          Couldn't load the review queue: {error}
        </div>
        <button style={{ ...bulkBtnStyle, marginTop: 12 }} onClick={fetchItems}>
          Retry
        </button>
      </div>
    )
  }

  if (items.length === 0 && dataRequests.length === 0) {
    return (
      <div style={pageStyle}>
        <h1 style={headingStyle}>Listing Review</h1>
        <div style={{ color: 'var(--text-2)', fontFamily: 'var(--sans)', fontSize: 14, marginTop: 32 }}>
          All listings in sync. Last checked{' '}
          <span style={{ fontFamily: 'var(--mono)' }}>
            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
          .
        </div>
      </div>
    )
  }

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
        <h1 style={headingStyle}>Listing Review</h1>
        <span style={countBadgeStyle}>{items.length + dataRequests.length} pending</span>
      </div>

      {dataRequests.length > 0 && (
        <section style={{ marginBottom: 32 }} data-testid="competitor-data-request-queue">
          <h2 style={sectionHeadingStyle}>
            Competitor data requests
            <span style={groupCountBadgeStyle}>{dataRequests.length}</span>
          </h2>
          {dataRequests.map(item => (
            <div key={item.id} style={rowStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ color: 'var(--text-1)', fontSize: 14, fontWeight: 600 }}>{item.competitor_name}</div>
                  <div style={{ color: 'var(--text-2)', fontSize: 12, marginTop: 2 }}>
                    {item.organization_name} · requested {timeAgo(item.created_at)}
                    {item.last_observed_at ? ` · last observed ${timeAgo(item.last_observed_at)}` : ' · no usable menu yet'}
                  </div>
                  {item.website_url && (
                    <a href={item.website_url} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 5, color: 'var(--accent)', fontSize: 11.5 }}>
                      Open public website ↗
                    </a>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                  {item.status === 'pending' && (
                    <button style={approveBtnStyle(resolving.has(item.id))} disabled={resolving.has(item.id)} onClick={() => handleDataRequest(item.id, 'reviewing')}>
                      Review
                    </button>
                  )}
                  <button style={approveBtnStyle(resolving.has(item.id))} disabled={resolving.has(item.id)} onClick={() => handleDataRequest(item.id, 'resolved')}>
                    Resolved
                  </button>
                  <button style={rejectBtnStyle(resolving.has(item.id))} disabled={resolving.has(item.id)} onClick={() => handleDataRequest(item.id, 'dismissed')}>
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Bulk approve bar */}
      {items.length > 0 && <div style={bulkBarStyle}>
        <span style={{ color: 'var(--text-2)', fontFamily: 'var(--sans)', fontSize: 13, whiteSpace: 'nowrap' }}>
          Bulk approve
        </span>
        <select
          style={selectStyle}
          value={bulkCounty}
          onChange={e => { setBulkCounty(e.target.value); setBulkConfirming(false) }}
        >
          <option value="">All counties</option>
          {counties.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          style={selectStyle}
          value={bulkType}
          onChange={e => { setBulkType(e.target.value); setBulkConfirming(false) }}
        >
          <option value="">All types</option>
          {GROUP_ORDER.map(ct => <option key={ct} value={ct}>{GROUP_LABELS[ct]}</option>)}
        </select>

        {!bulkConfirming ? (
          <button
            style={{ ...bulkBtnStyle, opacity: bulkCount === 0 ? 0.5 : 1 }}
            onClick={() => setBulkConfirming(true)}
            disabled={bulkCount === 0}
          >
            Approve{' '}
            <span style={{ fontFamily: 'var(--mono)' }}>{bulkCount}</span>
            {' '}matching
          </button>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'var(--text-1)', fontFamily: 'var(--sans)', fontSize: 13 }}>
              Approve{' '}
              <span style={{ fontFamily: 'var(--mono)' }}>{bulkCount}</span>
              {' '}items?
            </span>
            <button
              style={{ ...bulkBtnStyle, background: 'rgba(29,158,117,0.15)', color: 'var(--accent-intel)', borderColor: 'rgba(29,158,117,0.3)' }}
              onClick={handleBulkApprove}
              disabled={bulkWorking}
            >
              {bulkWorking ? 'Approving…' : 'Confirm'}
            </button>
            <button
              style={cancelBtnStyle}
              onClick={() => setBulkConfirming(false)}
              disabled={bulkWorking}
            >
              Cancel
            </button>
          </div>
        )}
      </div>}

      {/* Grouped feed */}
      {grouped.map(group => (
        <section key={group.key} style={{ marginBottom: 32 }}>
          <h2 style={sectionHeadingStyle}>
            {group.label}
            <span style={groupCountBadgeStyle}>{group.items.length}</span>
          </h2>
          {group.items.slice(0, visibleByGroup[group.key] ?? 20).map(item => (
            <ItemRow
              key={item.id}
              item={item}
              resolving={resolving.has(item.id)}
              onAction={handleAction}
            />
          ))}
          {(visibleByGroup[group.key] ?? 20) < group.items.length && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button
                style={cancelBtnStyle}
                onClick={() => setVisibleByGroup(prev => ({ ...prev, [group.key]: Math.min((prev[group.key] ?? 20) + 20, group.items.length) }))}
              >
                Show 20 more
              </button>
              <button
                style={cancelBtnStyle}
                onClick={() => setVisibleByGroup(prev => ({ ...prev, [group.key]: group.items.length }))}
              >
                Show all {group.items.length}
              </button>
            </div>
          )}
        </section>
      ))}
    </div>
  )
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
  padding: '32px 40px',
  maxWidth: 860,
  margin: '0 auto',
}

const headingStyle: React.CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: 22,
  fontWeight: 600,
  color: 'var(--text-1)',
  margin: 0,
}

const countBadgeStyle: React.CSSProperties = {
  background: 'var(--surface-3)',
  color: 'var(--text-2)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '2px 10px',
  fontFamily: 'var(--mono)',
  fontSize: 12,
}

const bulkBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '12px 16px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  marginBottom: 28,
  flexWrap: 'wrap',
}

const selectStyle: React.CSSProperties = {
  background: 'var(--surface-3)',
  color: 'var(--text-1)',
  border: '1px solid var(--border)',
  borderRadius: 4,
  padding: '4px 8px',
  fontSize: 13,
  fontFamily: 'var(--sans)',
  cursor: 'pointer',
  outline: 'none',
}

const bulkBtnStyle: React.CSSProperties = {
  background: 'var(--surface-3)',
  color: 'var(--text-1)',
  border: '1px solid var(--border)',
  borderRadius: 4,
  padding: '5px 14px',
  fontSize: 13,
  fontFamily: 'var(--sans)',
  cursor: 'pointer',
}

const cancelBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--text-2)',
  border: '1px solid var(--border)',
  borderRadius: 4,
  padding: '5px 12px',
  fontSize: 13,
  fontFamily: 'var(--sans)',
  cursor: 'pointer',
}

const sectionHeadingStyle: React.CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-2)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: 8,
  borderBottom: '1px solid var(--border)',
  paddingBottom: 8,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}

const groupCountBadgeStyle: React.CSSProperties = {
  fontFamily: 'var(--mono)',
  fontSize: 11,
  background: 'var(--surface-3)',
  color: 'var(--text-2)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '1px 6px',
  fontWeight: 400,
}

const rowStyle: React.CSSProperties = {
  padding: '12px 14px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  marginBottom: 5,
}

const diffRowStyle: React.CSSProperties = {
  marginTop: 6,
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 2,
}

const approveBtnStyle = (disabled: boolean): React.CSSProperties => ({
  background: disabled ? 'var(--surface-3)' : 'rgba(29,158,117,0.13)',
  color: disabled ? 'var(--text-2)' : 'var(--accent-intel)',
  border: `1px solid ${disabled ? 'var(--border)' : 'rgba(29,158,117,0.28)'}`,
  borderRadius: 4,
  padding: '5px 12px',
  fontSize: 12,
  fontFamily: 'var(--sans)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'opacity 0.12s',
  minWidth: 72,
})

const rejectBtnStyle = (disabled: boolean): React.CSSProperties => ({
  background: disabled ? 'var(--surface-3)' : 'rgba(212,83,126,0.10)',
  color: disabled ? 'var(--text-2)' : 'var(--accent-alert)',
  border: `1px solid ${disabled ? 'var(--border)' : 'rgba(212,83,126,0.24)'}`,
  borderRadius: 4,
  padding: '5px 12px',
  fontSize: 12,
  fontFamily: 'var(--sans)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'opacity 0.12s',
  minWidth: 72,
})
